/**
 * Chat Animations
 *
 * Animation components and utilities for smooth UX.
 */

import React from 'react';
import { Box, Fade, Slide, Grow, Zoom, Collapse } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

/**
 * Message Entry Animation
 * Smooth slide-in animation for new messages
 */
interface MessageAnimationProps {
  children: React.ReactNode;
  isUser?: boolean;
  delay?: number;
}

export const MessageAnimation: React.FC<MessageAnimationProps> = ({
  children,
  isUser = false,
  delay = 0,
}) => {
  const { classes, cx } = useStyles();
  const [show, setShow] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <Slide direction={isUser ? 'left' : 'right'} in={show} timeout={300}>
      <Fade in={show} timeout={400}>
        <Box className={cx(classes.messageAnimation, isUser && 'user')}>
          {children}
        </Box>
      </Fade>
    </Slide>
  );
};

/**
 * Card Reveal Animation
 * Grow effect for interactive cards
 */
interface CardAnimationProps {
  children: React.ReactNode;
  delay?: number;
}

export const CardAnimation: React.FC<CardAnimationProps> = ({
  children,
  delay = 0,
}) => {
  const [show, setShow] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <Grow in={show} timeout={400} style={{ transformOrigin: '0 0' }}>
      <Box>{children}</Box>
    </Grow>
  );
};

/**
 * Panel Animation
 * Smooth open/close for chat panel
 */
interface PanelAnimationProps {
  children: React.ReactNode;
  open: boolean;
  position?: 'bottom-right' | 'center' | 'fullscreen';
}

export const PanelAnimation: React.FC<PanelAnimationProps> = ({
  children,
  open,
  position = 'bottom-right',
}) => {
  const direction = position === 'bottom-right' ? 'up' : 'down';

  return (
    <Slide direction={direction} in={open} timeout={300}>
      <Zoom in={open} timeout={250}>
        <Box>{children}</Box>
      </Zoom>
    </Slide>
  );
};

/**
 * Staggered List Animation
 * Animate list items with staggered delays
 */
interface StaggeredListProps {
  children: React.ReactNode[];
  staggerDelay?: number;
}

export const StaggeredList: React.FC<StaggeredListProps> = ({
  children,
  staggerDelay = 50,
}) => {
  return (
    <>
      {React.Children.map(children, (child, index) => (
        <Fade in timeout={300} style={{ transitionDelay: `${index * staggerDelay}ms` }}>
          <Box>{child}</Box>
        </Fade>
      ))}
    </>
  );
};

/**
 * Expand/Collapse Animation
 * For sections that expand/collapse
 */
interface ExpandAnimationProps {
  children: React.ReactNode;
  expanded: boolean;
}

export const ExpandAnimation: React.FC<ExpandAnimationProps> = ({
  children,
  expanded,
}) => {
  return (
    <Collapse in={expanded} timeout={300}>
      {children}
    </Collapse>
  );
};

/**
 * Pulse Animation Component
 * For attention-grabbing elements
 */
interface PulseAnimationProps {
  children: React.ReactNode;
  active?: boolean;
}

export const PulseAnimation: React.FC<PulseAnimationProps> = ({
  children,
  active = true,
}) => {
  const { classes, cx } = useStyles();

  return (
    <Box className={cx(classes.pulse, active && 'active')}>
      {children}
    </Box>
  );
};

/**
 * Shake Animation Component
 * For error feedback
 */
interface ShakeAnimationProps {
  children: React.ReactNode;
  shake: boolean;
}

export const ShakeAnimation: React.FC<ShakeAnimationProps> = ({
  children,
  shake,
}) => {
  const { classes, cx } = useStyles();

  return (
    <Box className={cx(classes.shake, shake && 'active')}>
      {children}
    </Box>
  );
};

/**
 * Success Checkmark Animation
 */
export const SuccessAnimation: React.FC = () => {
  const { classes } = useStyles();

  return (
    <Box className={classes.successContainer}>
      <svg className={classes.successSvg} viewBox="0 0 52 52">
        <circle
          className={classes.successCircle}
          cx="26"
          cy="26"
          r="25"
          fill="none"
        />
        <path
          className={classes.successCheck}
          fill="none"
          d="M14.1 27.2l7.1 7.2 16.7-16.8"
        />
      </svg>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  messageAnimation: {
    '&.user': {
      // User-specific animation styles
    },
  },

  pulse: {
    '&.active': {
      animation: 'pulse 2s infinite',
    },
    '@keyframes pulse': {
      '0%': {
        transform: 'scale(1)',
      },
      '50%': {
        transform: 'scale(1.05)',
      },
      '100%': {
        transform: 'scale(1)',
      },
    },
  },

  shake: {
    '&.active': {
      animation: 'shake 0.5s ease-in-out',
    },
    '@keyframes shake': {
      '0%, 100%': {
        transform: 'translateX(0)',
      },
      '10%, 30%, 50%, 70%, 90%': {
        transform: 'translateX(-4px)',
      },
      '20%, 40%, 60%, 80%': {
        transform: 'translateX(4px)',
      },
    },
  },

  successContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 52,
    height: 52,
  },

  successSvg: {
    width: 52,
    height: 52,
  },

  successCircle: {
    stroke: theme.palette.success.main,
    strokeWidth: 2,
    strokeDasharray: 166,
    strokeDashoffset: 166,
    animation: 'stroke 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards',
    '@keyframes stroke': {
      '100%': {
        strokeDashoffset: 0,
      },
    },
  },

  successCheck: {
    stroke: theme.palette.success.main,
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    strokeDasharray: 48,
    strokeDashoffset: 48,
    animation: 'stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.5s forwards',
  },
}));

export default MessageAnimation;

